import { Box, Stack, Typography } from "@mui/material";

interface Props {
	dependentEligibility: any;
}

const DependentEligibilityCard = ({ dependentEligibility }: Props) => {
	const hasData = dependentEligibility?.is_present;

	// If no data, return simple Typography without border
	if (!hasData) {
		return (
			<Typography fontSize={14} color="text.secondary">
				N/A
			</Typography>
		);
	}

	// Render the full card only if data exists
	return (
		<Box
			sx={{
				p: 2,
				borderRadius: 2,
				backgroundColor: "#fefefe",
				border: "1px solid #e5e7eb",
				display: "flex",
				flexDirection: "column",
				gap: 1.5
			}}
		>
			<Typography fontWeight={600} fontSize={15}>
				Dependent Eligibility
			</Typography>
			<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
				{/* Eligible Relationships */}
				<Section title="Eligible Relationships">
					{dependentEligibility.eligible_relationship?.length ? (
						<Stack direction="row" flexWrap="wrap" gap={0.75}>
							{dependentEligibility.eligible_relationship.map((rel: string, idx: number) => (
								<Tag key={idx} label={rel} color="success" />
							))}
						</Stack>
					) : (
						<Typography fontSize={14} color="text.secondary">
							N/A
						</Typography>
					)}
				</Section>

				{/* Rules */}
				<Section title="Dependent Eligibility for the eligible dependent">
					<Stack direction={{ xs: "column", md: "row" }} flexWrap="wrap" gap={1.5}>
						<RuleCard
							title="Spouse"
							minAge={dependentEligibility.spouse?.min_age}
							maxAge={dependentEligibility.spouse?.max_age}
							exceed={dependentEligibility.spouse?.can_spouse_has_max_benefit_amount_than_employee}
						/>
						<RuleCard
							title="Children"
							minAge={dependentEligibility.children?.min_age}
							maxAge={dependentEligibility.children?.max_age}
							exceed={dependentEligibility.children?.can_children_has_max_benefit_amount_than_employee}
						/>
						{dependentEligibility.children?.disabled && (
							<RuleCard
								title="Disabled Children"
								minAge={dependentEligibility.children.disabled.min_age}
								maxAge={dependentEligibility.children.disabled.max_age}
								exceed={
									dependentEligibility.children.disabled
										.can_children_has_max_benefit_amount_than_employee
								}
							/>
						)}
					</Stack>
				</Section>
			</Box>
		</Box>
	);
};

const Section = ({ title, children }: any) => (
	<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
		<Typography fontWeight={600} fontSize={13} color="text.secondary">
			{title}
		</Typography>
		{children}
	</Box>
);

const RuleCard = ({ title, minAge, maxAge, exceed }: any) => (
	<Box
		sx={{
			p: 1.25,
			borderRadius: 1,
			backgroundColor: "#f9fafb",
			minWidth: 150,
			flex: 1,
			boxShadow: "0 1px 2px rgba(0,0,0,0.06)"
		}}
	>
		<Typography fontWeight={600} fontSize={14.5} gutterBottom>
			{title}
		</Typography>

		<Typography fontSize={13}>
			Age Range:{" "}
			<b>
				{minAge} – {maxAge}
			</b>
		</Typography>

		<Typography fontSize={13} color={exceed ? "success.main" : "text.secondary"}>
			Max Benefit Exceeds Employee: {exceed ? "Yes" : "No"}
		</Typography>
	</Box>
);

const Tag = ({ label, color }: { label: string; color?: "success" | "error" }) => (
	<Box
		sx={{
			px: 1.35,
			py: 0.5,
			borderRadius: 1,
			backgroundColor: color === "success" ? "#f0fdf4" : "#fef3f2",
			color: color === "success" ? "#166534" : "#9b1c1c",
			fontSize: 14.5,
			fontWeight: 500,
			lineHeight: 1.35,
			whiteSpace: "nowrap",
			boxShadow: "0 1px 1px rgba(0,0,0,0.05)"
		}}
	>
		{label}
	</Box>
);

export default DependentEligibilityCard;
